import * as React from 'react';
import type {
  TrackingDataV2,
  TrackingEventV2,
  TrackingServiceV2,
} from '@oneaudi/audi-tracking-service';
import { APP_NAME } from './utils/utils';

// Tracking component names should be static and not change if feature app name changes
const COMPONENT_NAME_WITH_DASH = 'editorial-accordion';
const COMPONENT_NAME_WITH_SPACE = 'editorial accordion';

type EventAction =
  | 'page_load'
  | 'step'
  | 'opt-in'
  | 'view_change'
  | 'internal_link'
  | 'impression'
  | 'feature_app_ready'
  | 'exit_link'
  | 'download'
  | 'image'
  | 'video'
  | 'configuration'
  | 'conflict'
  | 'favorite'
  | 'call'
  | 'anchor'
  | 'email'
  | 'content'
  | 'navigation'
  | 'tooltip'
  | 'error'
  | 'filter'
  | 'form_field'
  | 'non_interaction'
  | 'print'
  | 'user_account_change'
  | 'rating'
  | 'compare'
  | 'order';

export type TrackingClickEvent = {
  name?: string;
} & {
  linkLabel: string;
  linkUrl: string;
  elementName: 'text link' | 'other';
  headline: string;
  clickEventName: string;
  sectionAction?: string;
};

export type TrackClickEvent = (event: TrackingClickEvent) => void;

type CreateEventProps = {
  attributes?: TrackingEventV2['attributes'];
  action: EventAction;
  name: string;
};

const createEvent = ({ attributes, action, name }: CreateEventProps): TrackingDataV2 => ({
  event: {
    attributes: {
      componentName: APP_NAME,
      ...attributes,
    },
    eventInfo: {
      eventAction: action,
      eventName: name,
    },
  },
});

const trackClickEvent = (
  tracking: TrackingServiceV2,
  { linkUrl, linkLabel, elementName, headline, clickEventName, sectionAction }: TrackingClickEvent,
): void => {
  const eventAction =
    tracking.evaluateLinkType(linkUrl) || (linkUrl && 'internal_link') || sectionAction;
  if (!eventAction) {
    // do not track click event if link type could not be evaluated
    return;
  }

  const event = {
    event: {
      attributes: {
        componentName: COMPONENT_NAME_WITH_DASH,
        label: linkLabel,
        clickID: '',
        pos: '',
        targetURL: linkUrl,
        value: headline,
        elementName,
      },
      eventInfo: {
        // if it is open/close accordion event - we get "sectionAction", linkUrl not provided
        eventAction: (linkUrl || sectionAction) && eventAction,
        eventName: `${COMPONENT_NAME_WITH_SPACE} - ${clickEventName}`,
      },
    },
  };
  tracking.track(event);
};

export const useTracking = (
  service?: TrackingServiceV2,
): {
  service: TrackingServiceV2 | undefined;
  registerImpressionTracking: (
    el: React.MutableRefObject<null>,
    attributes: TrackingEventV2['attributes'],
  ) => void;
  sendReadyEvent: () => void;
  sendClickEvent: (event: TrackingClickEvent) => void;
} => {
  if (typeof window === 'undefined' || typeof service === 'undefined') {
    // skip tracking initialization when no window object is present (=> SSR)
    return {
      service: undefined,
      registerImpressionTracking: () => null,
      sendReadyEvent: () => null,
      sendClickEvent: () => null,
    };
  }

  const sendReadyEvent = React.useCallback(() => {
    const attribute = {
      action: 'feature_app_ready' as EventAction,
      name: `${COMPONENT_NAME_WITH_SPACE} - feature app ready`,
      attributes: {
        componentName: COMPONENT_NAME_WITH_DASH,
        elementName: '',
        label: '',
        clickID: '',
        pos: '',
        targetURL: '',
        value: '',
      },
    };
    const toBeTracked = createEvent(attribute);
    service.track(toBeTracked as unknown as TrackingDataV2);
  }, [service]);

  const registerImpressionTracking = React.useCallback(
    (ref: React.MutableRefObject<null>, attributes: TrackingEventV2['attributes']) => {
      if (service && ref?.current) {
        service.registerImpressionTracking(ref.current, () => ({
          event: {
            eventInfo: {
              eventAction: 'impression',
              eventName: `${COMPONENT_NAME_WITH_SPACE} - impression`,
            },
            attributes: {
              componentName: COMPONENT_NAME_WITH_DASH,
              label: '',
              targetURL: '',
              clickID: '',
              elementName: '',
              pos: '',
              ...attributes,
            },
          },
        }));
      }
    },
    [service],
  );

  const sendClickEvent = React.useCallback((event: TrackingClickEvent) => {
    trackClickEvent(service, event);
  }, []);

  return {
    service,
    registerImpressionTracking,
    sendReadyEvent,
    sendClickEvent,
  };
};
